import React, { useEffect, useRef } from "react";
import {
  Col,
  Flex,
  Form,
  FormListFieldData,
  Input,
  InputNumber,
  Row,
} from "antd";
import MarkdownEditor from "@uiw/react-markdown-editor";
import { useAppSelector } from "../redux/hooks";
import { DeleteIcon } from "../assets";
import Select from "../components/Select";
import { useWatch } from "antd/es/form/Form";
import { IconPrimary } from "../components";
import CategoryManager from "../components/CategoryManager";
import { getCategoryState } from "../redux/store/getState";
import FileUpload from "../components/FileUpload";
import CorrectOptionPicker from "../components/CorrectOptionPicker";

type Props = {
  field: FormListFieldData;
  onDelete: (index: number | number[]) => void;
  form: any;
};
const QuestionForm: React.FC<Props> = ({ field, onDelete, form }: Props) => {
  const { categories } = useAppSelector(getCategoryState);

  const questionTypeWater = useWatch(["questions", field.name, "type"], form);
  const optionValues = useWatch(["questions", field.name, "option"], form);

  const formLayout = { xs: 24, sm: 12, md: 12, lg: 12 };

  useEffect(() => {
    if (questionTypeWater === "mcq") {
      form.setFieldValue(
        ["questions", field.name, "option"],
        form.getFieldValue(["questions", field.name, "option"]) || [
          "",
          "",
          "",
          "",
        ]
      );
    }

    if (questionTypeWater === "flag") {
      form.setFieldValue(["questions", field.name, "option"], null);
    }
  }, [questionTypeWater, form, field]);

  // The answer is stored as the option's text, so editing an option that is
  // already marked correct has to carry the answer along with it — otherwise
  // the exam would keep grading against text no option holds any more.
  const previousOptionsRef = useRef<string[]>([]);
  useEffect(() => {
    if (questionTypeWater !== "mcq") {
      previousOptionsRef.current = [];
      return;
    }

    const previous = previousOptionsRef.current;
    const current = (optionValues || []).map((option: any) =>
      String(option ?? "").trim()
    );
    previousOptionsRef.current = current;

    // Nothing typed yet (first render of a saved question) — leave the answer be.
    if (!current.some(Boolean)) return;

    const answer = String(
      form.getFieldValue(["questions", field.name, "answer"]) ?? ""
    ).trim();
    if (!answer || current.includes(answer)) return;

    // Only follow an edit we can attribute to the option that was marked
    // correct. Anything else (a legacy answer that never matched) is left for
    // the picker to flag, rather than wiped without the examiner noticing.
    const renamedAt = previous.indexOf(answer);
    if (renamedAt < 0) return;

    form.setFieldValue(
      ["questions", field.name, "answer"],
      current[renamedAt] || undefined
    );
  }, [optionValues, questionTypeWater, form, field.name]);

  return (
    <>
      <Flex className="full-width-div" justify="space-between">
        <h5>{`Question ${field.name + 1} .`}</h5>
        <IconPrimary onClick={() => onDelete(field.name)}>
          <DeleteIcon />
        </IconPrimary>
      </Flex>
      <Row gutter={[20, 20]}>
        <Col {...formLayout}>
          <Select
            options={[
              { id: "flag", name: "Flag" },
              { id: "mcq", name: "MCQ" },
            ]}
            formItemProps={{
              label: "Select Question Type",
              name: [field.name, "type"],
              rules: [{ required: true, message: "Question Type is required" }],
            }}
            dropdownRender={null}
          />
        </Col>

        <Col {...formLayout}>
          <Select
            options={categories}
            formItemProps={{
              label: "Select Category",
              name: [field.name, "category", "id"],
              rules: [{ required: true, message: "Category is required" }],
            }}
            dropdownRender={<CategoryManager />}
          />
        </Col>
      </Row>
      <div data-color-mode="dark">
        <Form.Item
          name={[field.name, "description"]}
          label="Description"
          rules={[
            {
              required: true,
              message: "Missing question description",
            },
          ]}
        >
          <MarkdownEditor height="150px" />
        </Form.Item>
      </div>

      <Form.List name={[field.name, "option"]}>
        {(fields) => (
          <Row gutter={[10, 10]} style={{ marginBottom: 20 }}>
            {fields.map((field, index) => (
              <Col key={field.key}>
                <Form.Item label={`Option ${index + 1}`} name={[field.name]}>
                  <Input />
                </Form.Item>
              </Col>
            ))}
          </Row>
        )}
      </Form.List>

      {questionTypeWater === "mcq" ? (
        <Form.Item
          name={[field.name, "answer"]}
          label="Correct Option"
          rules={[
            {
              required: true,
              message: "Pick the correct option",
            },
            {
              validator: (_: any, value: any) => {
                const answer = String(value ?? "")
                  .trim()
                  .toLowerCase();
                if (!answer) return Promise.resolve();

                const texts = (optionValues || [])
                  .map((option: any) =>
                    String(option ?? "")
                      .trim()
                      .toLowerCase()
                  )
                  .filter(Boolean);

                return texts.includes(answer)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error("Pick the correct option from the list above")
                    );
              },
            },
          ]}
        >
          <CorrectOptionPicker options={optionValues} />
        </Form.Item>
      ) : null}

      <Row gutter={[20, 20]}>
        {questionTypeWater === "mcq" ? null : (
          <Col {...formLayout}>
            <Form.Item
              name={[field.name, "answer"]}
              label="Flag"
              rules={[
                {
                  required: true,
                  message: "Missing question flag",
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        )}

        <Col {...formLayout}>
          <Form.Item
            name={[field.name, "score"]}
            label={"Score"}
            rules={[
              {
                required: true,
                message: "Missing question score",
              },
            ]}
          >
            <InputNumber type="number" />
          </Form.Item>
        </Col>
        <FileUpload
          form={form}
          name={[field.name, "file"]}
          parentField={["questions", field.name, "file"]}
          file_type="question_file"
        />
      </Row>
    </>
  );
};

export default QuestionForm;
